import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { createCategory, updateCategory, updateCategoryParent, deleteCategory, toggleCategoryHidden, updateCategoryColor, reorderCategories } from '@/lib/api'
import { fetchCategoriesData } from '@/lib/api/helpers'
import { mutationErrorHandler } from '@/lib/utils'
import type { CategoryRow } from '@/lib/db-types'

export type Category = CategoryRow

export function useCategories() {
  return useQuery<Category[]>({
    queryKey: ['categories'],
    queryFn: fetchCategoriesData,
  })
}

export function useCreateCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ name, parentId, ownerId }: { name: string; parentId: string; ownerId?: string | null }) =>
      createCategory(name, parentId, ownerId),
    onSuccess: () => {
      toast.success('カテゴリを追加しました')
      queryClient.invalidateQueries({ queryKey: ['categories'] })
    },
  })
}

export function useRenameCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      updateCategory(id, name),
    onSuccess: () => {
      toast.success('カテゴリ名を変更しました')
      queryClient.invalidateQueries({ queryKey: ['categories'] })
    },
    onError: mutationErrorHandler('変更に失敗しました'),
  })
}

export function useDeleteCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteCategory,
    onSuccess: () => {
      toast.success('カテゴリを削除しました')
      queryClient.invalidateQueries({ queryKey: ['categories'] })
    },
    onError: mutationErrorHandler('削除に失敗しました'),
  })
}

export function useToggleCategoryHidden() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, isHidden }: { id: string; isHidden: boolean }) =>
      toggleCategoryHidden(id, isHidden),
    onMutate: async ({ id, isHidden }) => {
      await queryClient.cancelQueries({ queryKey: ['categories'] })
      const previous = queryClient.getQueryData<Category[]>(['categories'])
      queryClient.setQueryData<Category[]>(['categories'], (old) =>
        old?.map((c) => (c.id === id ? { ...c, is_hidden: isHidden } : c))
      )
      return { previous }
    },
    onSuccess: (_data, variables) => {
      toast.success(variables.isHidden ? 'カテゴリを非表示にしました' : 'カテゴリを表示しました')
    },
    onError: (error, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['categories'], context.previous)
      }
      mutationErrorHandler('変更に失敗しました')(error)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] })
    },
  })
}

export function useReorderCategories() {
  return useMutation({
    mutationFn: reorderCategories,
    onError: mutationErrorHandler('並び順の保存に失敗しました'),
  })
}

export function useUpdateCategoryParent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, parentId }: { id: string; parentId: string }) =>
      updateCategoryParent(id, parentId),
    onSuccess: () => {
      toast.success('カテゴリの分類を変更しました')
      queryClient.invalidateQueries({ queryKey: ['categories'] })
    },
    onError: mutationErrorHandler('変更に失敗しました'),
  })
}

export function useUpdateCategoryColor() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, color }: { id: string; color: string }) =>
      updateCategoryColor(id, color),
    onMutate: async ({ id, color }) => {
      await queryClient.cancelQueries({ queryKey: ['categories'] })
      const previous = queryClient.getQueryData<Category[]>(['categories'])
      queryClient.setQueryData<Category[]>(['categories'], (old) =>
        old?.map((c) => (c.id === id ? { ...c, color } : c))
      )
      return { previous }
    },
    onError: (error, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['categories'], context.previous)
      }
      mutationErrorHandler('色の変更に失敗しました')(error)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] })
    },
  })
}
